/**
 * @file Guarantors — the third party who stands behind a tenancy, the consent
 * fact that gates ever contacting them, and the guard a future notification path
 * must call (PLAN.md §10 T2.9, §5's `guarantor_contacts`).
 *
 * ## The product law this file exists to hold
 *
 * `_factory/conventions.md` §Product law, verbatim:
 *
 *   - "No SMS to any number not opted-in via the portal (TCPA)."
 *   - "Guarantor notifications are blocked unless the guarantor record has
 *     consent_recorded = true."
 *   - "Communications pass the prohibited-pattern registry (no threats, shame,
 *     debt-collection framing, eviction-adjacent language, legal conclusions).
 *     CI-tested."
 *
 * Two things follow, and both are encoded here rather than promised in prose:
 *
 *   1. **`mayContactGuarantor`** — the consent gate. A guarantor who never agreed
 *      to be contacted must not be contacted, and the record is the ONLY place
 *      that fact lives. It is a pure function over two fields so it can be tested
 *      in both directions without a database (see the suite), and it is what the
 *      API computes onto every row it returns, so no consumer — including a
 *      notification path that does not exist yet — has to re-derive the rule.
 *   2. **`findProhibitedPhrasing`** — the prohibited-pattern check. §10 T2.9 names
 *      a "guarantor ruleset in the registry"; **no notification registry exists in
 *      this repo** (see below), so what ships here is the check itself plus the
 *      handful of user-facing strings this task introduces, asserted clean by the
 *      suite. It is deliberately NOT a template library.
 *
 * ## What is NOT built here, and why that is the honest scope
 *
 * **Nothing sends anything.** Notifications (T1.8) do not exist: there is no
 * outbox drain for a guarantor, no template renderer, no SMS rail. PLAN.md §10
 * T2.9 is "Guarantor escalation … neutral templates, guarantor ruleset in the
 * registry", and every part of that sentence is a policy about MESSAGES. Writing
 * escalation tiers or neutral copy before the transport exists would produce inert
 * strings pretending to be a feature, and would be inventing founder-owned policy
 * (timing, who is reached, when) that nobody has decided. So:
 *
 *   - `escalation_rule` stays `{}` — an empty placeholder with a CHECK that it is
 *     an object (migration 0011), and no behaviour anywhere reads it;
 *   - there are **zero message templates** in this file, and therefore nothing to
 *     render;
 *   - `GUARANTOR_CONTACT_NOT_BUILT_REASON` is the sentence the UI shows saying so.
 *
 * When T1.8 lands, its guarantor path calls `mayContactGuarantor(row)` **before**
 * anything is enqueued, and routes its copy through `findProhibitedPhrasing`. The
 * API already computes the guard's verdict onto every row (`GuarantorContactDto.
 * mayContact`), so the check is the surface's, not a caller's good intentions.
 *
 * ## Consent is a FACT, and only an explicit act records it
 *
 * `consentRecorded` defaults to `false` in the database
 * (`packages/db/migrations/0011_guarantors.sql`), is never inferred from a
 * landlord having typed an email address, and is flipped by exactly one route:
 * `POST /workspaces/:id/guarantors/:guarantorId/consent`. The create route may set
 * it true only when the body also names a `consentSource`; PATCH cannot touch it
 * at all. Those rules are enforced in `apps/api/src/routes/guarantors.ts`, and
 * `mayContactGuarantor` is deliberately STRICTER than the law's literal reading —
 * see its own doc.
 *
 * The kernel discipline applies as everywhere in `packages/shared`: no drizzle, no
 * app code. The SPA imports the TYPES, the labels and the pure functions; the API
 * remains the authority on what may be written.
 */
import { z } from "zod";
import type { LeaseStatus } from "./leases.js";

// ---------------------------------------------------------------------------
// The consent rule — the load-bearing part
// ---------------------------------------------------------------------------

/**
 * The three states a guarantor's consent can be in, as the UI must render them.
 *
 * `unknown` is not decoration. `consent_recorded = true` with no
 * `consent_source` is a row the API refuses to write (a consent fact with no
 * recorded source is not a consent fact — the create route and the consent route
 * both refuse it) but the DATABASE can hold, because the pairing is a rule of the
 * write boundary, not of the column (migration 0011's header says why). Rather
 * than hide that, the state is named: the screen shows it, and the gate refuses
 * it. A derived value with no possible input would be the dishonest alternative.
 */
export const GUARANTOR_CONSENT_STATES = ["consented", "not_consented", "unknown"] as const;

export type GuarantorConsentState = (typeof GUARANTOR_CONSENT_STATES)[number];

/**
 * Human labels for the three states.
 *
 * Here rather than in the SPA for the same reason `EXPENSE_CATEGORY_LABELS` is:
 * the list, the filter chips and the counts all render them, and three different
 * spellings of "not consented" across one screen is how a consent state starts
 * disagreeing with itself. The wording is deliberately plain — "Consent not
 * sourced" states the missing fact rather than implying the person refused.
 */
export const GUARANTOR_CONSENT_STATE_LABELS: Readonly<Record<GuarantorConsentState, string>> = {
  consented: "Consented",
  not_consented: "Not consented",
  unknown: "Consent not sourced",
};

/** The two fields the consent rule reads. Structural, so a row or a literal fits. */
export interface GuarantorContactLike {
  /** `guarantor_contacts.consent_recorded`. `false` unless a person recorded it. */
  readonly consentRecorded: boolean;
  /** `guarantor_contacts.consent_source` — where the CURRENT state was learned from. */
  readonly consentSource: string | null;
}

/**
 * Derive what may be CLAIMED about a guarantor's consent from the record alone.
 *
 * The ordering is the rule:
 *
 *   * `false` → `not_consented`. Whatever a stray `consent_source` says, there is
 *     no consent, and nothing may be inferred from the presence of contact details.
 *   * `true` with a source → `consented`. Both facts are present, so the fact is
 *     complete.
 *   * `true` with a blank or missing source → `unknown`. The consent is asserted
 *     with nothing behind it, which is exactly the state the write boundary exists
 *     to prevent. A screen must not print "Consented" for it, and the gate refuses
 *     it.
 */
export function guarantorConsentState(contact: GuarantorContactLike): GuarantorConsentState {
  if (!contact.consentRecorded) return "not_consented";
  const source = contact.consentSource;
  return source === null || source.trim().length === 0 ? "unknown" : "consented";
}

/**
 * **The consent guard.** May this person be contacted at all?
 *
 * WHY this exists: `_factory/conventions.md` §Product law makes a guarantor's
 * `consent_recorded = true` the precondition for every notification, because a
 * guarantor is a third party who owes the platform nothing and never signed up for
 * it — an unwelcome message to them is both a product failure and, on the SMS
 * rail, a TCPA one. The record is the only place that fact lives, so the decision
 * to contact must read the record and nothing else: not a landlord's say-so, not
 * the presence of a phone number, not whether a balance is owed.
 *
 * It is PURE — two fields in, a boolean out — so both directions are unit tests
 * and no caller needs a database, a request or a clock to ask.
 *
 * **Deliberately narrower than the law's literal text**, and never wider: the law
 * blocks contact unless `consent_recorded = true`; this also refuses a `true` with
 * no `consent_source`, because a consent with no provenance is the state the write
 * boundary refuses to create and the UI refuses to claim (`unknown`). Refusing
 * more than the law requires is safe in the only direction that matters — it can
 * only ever stop a message.
 */
export function mayContactGuarantor(contact: GuarantorContactLike): boolean {
  return guarantorConsentState(contact) === "consented";
}

// ---------------------------------------------------------------------------
// The prohibited-pattern check
// ---------------------------------------------------------------------------

/**
 * The five categories `_factory/conventions.md` names, in its own words.
 *
 * A closed set because the conventions file closes it: "no threats, shame,
 * debt-collection framing, eviction-adjacent language, legal conclusions".
 */
export const PROHIBITED_PHRASING_CATEGORIES = [
  "threat",
  "shame",
  "debt_collection",
  "eviction_adjacent",
  "legal_conclusion",
] as const;

export type ProhibitedPhrasingCategory = (typeof PROHIBITED_PHRASING_CATEGORIES)[number];

export interface ProhibitedPhrasingRule {
  readonly category: ProhibitedPhrasingCategory;
  /** What the rule is looking for, for a report a person can read. */
  readonly label: string;
  /**
   * Non-global on purpose: a `/g` regex carries `lastIndex` between calls, so a
   * shared rule object would give a different answer depending on what ran before
   * it. These are matched with `exec` on a fresh scan.
   */
  readonly pattern: RegExp;
}

/**
 * The words a careless message reaches for, per category.
 *
 * **This is a guard on copy, not a classifier and not a legal opinion.** It is
 * anchored on the obvious vocabulary of each named category so that a template
 * that threatens, shames, frames a debt, gestures at eviction or states a legal
 * conclusion fails CI rather than failing a person. §10 T2.9's full "guarantor
 * ruleset in the registry" arrives with the notification registry itself (T1.8);
 * this ships the check and the strings this task adds, which is the part that can
 * be proved today.
 */
export const PROHIBITED_PHRASING_RULES: readonly ProhibitedPhrasingRule[] = [
  {
    category: "threat",
    label: "a threat, or a legal instrument named as one",
    pattern: /\b(?:or else|sue|lawsuit|prosecut\w*|press charges|legal action|attorney|lawyer|police)\b/i,
  },
  {
    category: "shame",
    label: "language that shames the person",
    pattern:
      /\b(?:shame\w*|embarrass\w*|humiliat\w*|deadbeat|freeloader|irresponsible|dishonest|liar)\b/i,
  },
  {
    category: "debt_collection",
    label: "debt-collection framing",
    pattern:
      /\b(?:debt collector|collection agency|garnish\w*|wage attachment|credit bureau|credit report|credit score|delinquen\w*)\b/i,
  },
  {
    category: "eviction_adjacent",
    label: "eviction-adjacent language",
    pattern:
      /\b(?:evict\w*|dispossess\w*|unlawful detainer|notice to quit|lock ?out|remove you from)\b/i,
  },
  {
    category: "legal_conclusion",
    label: "a legal conclusion the platform is not entitled to draw",
    pattern:
      /\b(?:you are in (?:default|breach)|legally (?:liable|required|obligated)|pursuant to|hereby (?:demand|notify)|violation of (?:the |your )?(?:lease|law))\b/i,
  },
];

/** One rule that matched, with the text that matched it. */
export interface ProhibitedPhrasing {
  readonly category: ProhibitedPhrasingCategory;
  readonly label: string;
  readonly match: string;
}

/**
 * Every prohibited pattern in `text`, as data.
 *
 * Empty means the copy is clean by the rules above. A caller that SENDS copy must
 * treat a non-empty result as a refusal; the shared suite asserts the user-facing
 * strings this task introduces return empty.
 */
export function findProhibitedPhrasing(text: string): readonly ProhibitedPhrasing[] {
  const found: ProhibitedPhrasing[] = [];
  for (const rule of PROHIBITED_PHRASING_RULES) {
    const match = rule.pattern.exec(text);
    if (match !== null) {
      found.push({ category: rule.category, label: rule.label, match: match[0] });
    }
  }
  return found;
}

// ---------------------------------------------------------------------------
// The user-facing copy this task introduces
// ---------------------------------------------------------------------------

/**
 * The sentence the Guarantors page shows about contacting: there is no such
 * capability yet, and consent is what will gate it when there is.
 *
 * A constant in the kernel rather than a string literal in the component for one
 * reason: it is COPY, so it is subject to the prohibited-pattern rule, and the
 * shared suite can only assert a string that has a single home. It says what is
 * true — nothing sends — instead of implying a button that would do nothing.
 */
export const GUARANTOR_CONTACT_NOT_BUILT_REASON =
  "Contacting a guarantor is not built yet — nothing in this product sends a " +
  "message to a guarantor. This record is what will decide it: consent recorded, " +
  "with the place it came from, and only then.";

/**
 * The consent rule in one sentence, for the screen and for any future surface.
 *
 * Also a constant for the same reason: it is copy, and the check must have a
 * subject.
 */
export const GUARANTOR_CONSENT_RULE_REASON =
  "A guarantor who has not agreed to be contacted must not be contacted, and this " +
  "record is the only place that agreement lives. Consent defaults to no, and " +
  "recording it is a separate, deliberate act.";

// ---------------------------------------------------------------------------
// Input schemas
// ---------------------------------------------------------------------------

/** Required, trimmed, length-bounded text. */
function requiredText(max: number) {
  return z.string().trim().min(1, "is required").max(max);
}

/**
 * Optional text stored as `null` when absent or blank — the same normalisation
 * `properties.ts`, `leases.ts`, `ledger.ts`, `maintenance.ts` and `expenses.ts`
 * use, and for the same reason: a cleared form field must not persist as `""`, or
 * "no phone number" gets two representations and every later query has to remember
 * both.
 */
function optionalText(max: number) {
  return z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((value) =>
      value === undefined || value === null || value.length === 0 ? null : value,
    );
}

/**
 * Optional email, normalised to `null` when blank, and validated LOOSELY.
 *
 * The blank case is allowed BEFORE the format check, not after (the trick
 * `leases.ts` documents at `optionalEmail`): a `.email()` first would reject `""`
 * with "must be an email address", so a landlord clearing the field would be told
 * they typed something wrong. The format itself is the permissive
 * `something@something.tld` shape — enough to catch a missing `@`, and deliberately
 * not an RFC 5322 parser, because rejecting a real address is the worse failure.
 */
function optionalEmail(max = 254) {
  return z
    .string()
    .trim()
    .max(max)
    .refine(
      (value) => value === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
      "must be an email address",
    )
    .nullish()
    .transform((value) =>
      value === undefined || value === null || value.length === 0 ? null : value,
    );
}

/**
 * Optional phone, normalised to `null` when blank, and validated LOOSELY on
 * purpose.
 *
 * The rule is "a plausible way to write a phone number": a permissive character set
 * (digits, spaces, and the punctuation real numbers carry — `+`, `-`, `(`, `)`,
 * `.`, `/`) and **at least seven digits**. That is the whole policy, and it is
 * stated rather than guessed at:
 *
 *   * No length normalisation, no country-code assumption, no `+1` requirement —
 *     inventing a strict national format would reject real numbers, which is a
 *     worse outcome than accepting an odd-looking one.
 *   * Letters are allowed only because extensions are written that way (`x123`,
 *     `ext. 4`); a value of pure letters fails the seven-digit rule.
 *   * Nothing here decides WHO may be messaged. A valid phone number is not
 *     consent — `mayContactGuarantor` is the only thing that decides that, and it
 *     reads `consent_recorded`.
 */
function optionalPhone(max = 40) {
  return z
    .string()
    .trim()
    .max(max)
    .refine(
      (value) => value === "" || /^[0-9A-Za-z+()\-./\s]+$/.test(value),
      "must be a phone number",
    )
    .refine(
      (value) => value === "" || (value.match(/\d/g)?.length ?? 0) >= 7,
      "must contain at least 7 digits",
    )
    .nullish()
    .transform((value) =>
      value === undefined || value === null || value.length === 0 ? null : value,
    );
}

/** `uuid`, as the ids in this schema are. */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * An optional uuid stored as `null` when absent or blank.
 *
 * Blank → `null` for the same reason text is: a select with "nobody" chosen must
 * not send `""` to a `uuid` column, and it must be possible to DETACH a guarantor
 * from a tenant — which is a real edit, not an omission.
 */
function optionalUuid(message: string) {
  return z
    .string()
    .trim()
    .refine((value) => value === "" || UUID_PATTERN.test(value), message)
    .nullish()
    .transform((value) =>
      value === undefined || value === null || value.length === 0 ? null : value,
    );
}

/**
 * The identifying fields of a guarantor contact — everything a PATCH may change.
 *
 * Consent is deliberately NOT in this shape, which is the structural half of the
 * rule that editing a phone number must not alter consent: the update schema has no
 * consent field to send, and the route refuses a body that carries one rather than
 * dropping it silently (`apps/api/src/routes/guarantors.ts`).
 */
const guarantorIdentitySchema = z.object({
  fullName: requiredText(120),
  email: optionalEmail(),
  phone: optionalPhone(),
  /**
   * The tenant this guarantor stands behind, or blank for "not linked yet". A
   * landlord collects a guarantor while putting a lease together, so the link is
   * genuinely optional.
   */
  leaseTenantId: optionalUuid("must be a tenant id"),
});

/**
 * POST /workspaces/:workspaceId/guarantors body.
 *
 * `consentRecorded` defaults to `false` and the body cannot talk its way past a
 * missing source: the `superRefine` refuses `consentRecorded: true` with no
 * `consentSource`, with a sentence rather than a database error. A `consentSource`
 * sent alongside `consentRecorded: false` is allowed and stored — "we asked, and
 * they declined; we learned that by phone" is a real record of a real
 * conversation, and `guarantorConsentState` still reads the state as
 * `not_consented`, which is what it is.
 */
export const guarantorContactCreateSchema = guarantorIdentitySchema
  .extend({
    consentRecorded: z.boolean().default(false),
    consentSource: optionalText(200),
  })
  .superRefine((value, ctx) => {
    if (value.consentRecorded && value.consentSource === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["consentSource"],
        message:
          "is required when consent is recorded — a consent fact with no recorded " +
          "source is not a consent fact",
      });
    }
  });

/**
 * PATCH /workspaces/:workspaceId/guarantors/:guarantorId body.
 *
 * `.partial()` on the identity fields only, so a field added to create is
 * updatable by default and cannot be forgotten — the rule `properties.ts` states
 * for its own update schema. **Consent is not here in any form**, which is why this
 * schema cannot flip it: the route additionally refuses a body that names a
 * consent field, so the refusal is a sentence rather than a silent no-op.
 */
export const guarantorContactUpdateSchema = guarantorIdentitySchema.partial();

/**
 * POST /workspaces/:workspaceId/guarantors/:guarantorId/consent body — the ONE
 * shape that records consent, or withdraws it.
 *
 * `source` is required to RECORD consent and optional to withdraw it, and the
 * asymmetry is deliberate: enabling the capability needs provenance, while
 * disabling it must never carry friction — a landlord reaching for the safe
 * direction should not be asked to justify it. A withdrawal with a source stores
 * it (that is where the withdrawal was learned); without one, `consent_source`
 * becomes `null`, because there is no current consent state to source.
 */
export const guarantorConsentSchema = z
  .object({
    /** `true` records consent; `false` withdraws it. */
    consented: z.boolean(),
    source: optionalText(200),
  })
  .superRefine((value, ctx) => {
    if (value.consented && value.source === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["source"],
        message:
          "is required when recording consent — a consent fact with no recorded " +
          "source is not a consent fact",
      });
    }
  });

export type GuarantorContactCreateInput = z.infer<typeof guarantorContactCreateSchema>;
export type GuarantorContactUpdateInput = z.infer<typeof guarantorContactUpdateSchema>;
export type GuarantorConsentInput = z.infer<typeof guarantorConsentSchema>;

/** The PRE-parse shapes, for the SPA to build request bodies from. */
export type GuarantorContactCreateBody = z.input<typeof guarantorContactCreateSchema>;
export type GuarantorContactUpdateBody = z.input<typeof guarantorContactUpdateSchema>;
export type GuarantorConsentBody = z.input<typeof guarantorConsentSchema>;

// ---------------------------------------------------------------------------
// DTOs — what the API returns
// ---------------------------------------------------------------------------

/**
 * One guarantor contact, as the API returns it.
 *
 * Declared here rather than inferred from the drizzle schema so the kernel does
 * not depend on a database package (dependency direction: apps → packages).
 *
 * The two derived fields are the point of the DTO rather than conveniences:
 * `consentState` is `guarantorConsentState` applied server-side and
 * `mayContact` is `mayContactGuarantor` applied server-side, so every consumer —
 * a screen, a future notification path, a test — reads the SAME rule the product
 * law names. `mayContact` is a PERMISSION, not a capability: nothing in this build
 * sends a message to anyone, and the screen says so
 * (`GUARANTOR_CONTACT_NOT_BUILT_REASON`).
 */
export interface GuarantorContactDto {
  readonly id: string;
  readonly fullName: string;
  readonly email: string | null;
  readonly phone: string | null;
  /** The raw fact. `false` unless a person recorded consent through the consent route. */
  readonly consentRecorded: boolean;
  /** Where the current consent state was learned from. */
  readonly consentSource: string | null;
  /** `guarantorConsentState` — what may be CLAIMED about this row's consent. */
  readonly consentState: GuarantorConsentState;
  /** `mayContactGuarantor` — the consent gate's verdict. A permission, not a sender. */
  readonly mayContact: boolean;
  /**
   * §5's escalation RULE slot. `{}` on every row that exists: whether there is an
   * escalation, who it reaches and when is founder-owned and deliberately unset
   * (migration 0011). Nothing in this build evaluates it.
   */
  readonly escalationRule: Readonly<Record<string, unknown>>;
  /** The tenant this guarantor stands behind, or NULL when the link is unset. */
  readonly leaseTenantId: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * A guarantor plus the context that makes them identifiable: the tenant they stand
 * behind, that tenant's lease, and where it is.
 *
 * Every context field is NULLABLE, and that is the ordinary state rather than a
 * missing join: `guarantor_contacts.lease_tenant_id` is nullable by design, so a
 * guarantor a landlord has collected before the lease exists is a complete row with
 * no context. The screen renders what is there and says nothing about what is not.
 */
export interface GuarantorListRowDto extends GuarantorContactDto {
  readonly leaseTenantName: string | null;
  readonly leaseId: string | null;
  readonly leaseStatus: LeaseStatus | null;
  readonly unitLabel: string | null;
  readonly propertyId: string | null;
  readonly propertyLabel: string | null;
  readonly addressLine1: string | null;
  readonly city: string | null;
  readonly state: string | null;
}

/**
 * GET /workspaces/:workspaceId/guarantors — every guarantor in the portfolio, and
 * the counts folded from the rows beside them.
 *
 * `counts.consented` is the number a landlord actually acts on: it is the count of
 * rows this build would be ALLOWED to contact (computed through
 * `mayContactGuarantor`, not by a second `consent_recorded` comparison), so a
 * header figure and the guard can never disagree. There is deliberately **no
 * "escalations pending" and no "next contact"**: nothing schedules either, and a
 * rendered zero would read as a fact about the world.
 */
export interface GuarantorListDto {
  /** True when the list was cut at the server cap: these are the first rows, not all of them. */
  readonly truncated?: true;
  readonly counts: {
    readonly guarantors: number;
    /** Rows `mayContactGuarantor` allows — the number the gate itself counts. */
    readonly consented: number;
    /** Rows linked to a tenant, i.e. that stand behind a real tenancy. */
    readonly linkedToTenants: number;
  };
  readonly guarantors: readonly GuarantorListRowDto[];
}
